import { useEffect, useRef, useState } from 'react';

/**
 * Bot defense layer.
 *
 * Loads Cloudflare Turnstile and exposes a small `window.__cabanaBotDefense`
 * API so the form-submit code can attach a Turnstile token to every lead
 * payload without coupling submit logic to the SDK.
 *
 * FingerprintJS Pro was removed on 2026-07-17. It rode along here and put a
 * visitor_id on every lead payload, but nothing consumed it: the only reader
 * was a Meta lead-quality dup-ring gate that shipped in shadow mode and never
 * enforced, and in 90 days it flagged exactly one device (the QA machine).
 * Turnstile is and always was the thing /api/lead-submit actually verifies.
 *
 * Why this lives at App-level (mounted once):
 *  - Turnstile loses its token when its host DOM node unmounts; rendering it
 *    inside an individual Slide would re-init it every navigation and refresh
 *    the challenge.
 *  - The form does multi-place submits (manual, auto-advance, inactivity
 *    timeout, browser-close beacon). Reading from a global getter keeps all
 *    of them in sync without prop drilling.
 *
 * Fail-open philosophy: if the SDK fails to load (ad-blocker, CSP, network),
 * the getter returns null and the form still submits. Server-side
 * verification in /api/lead-submit decides what to do with missing tokens.
 */

// The sitekey must be supplied via Vercel env vars in any non-dev environment.
// We deliberately do NOT hardcode prod fallback values: if envs go missing,
// the widget renders nothing and the server's fail-open path takes over,
// rather than silently emitting tokens scoped to the wrong sitekey/project.
// Cloudflare's documented test sitekey (1x0...AA) always passes and is used
// only when running `npm start` locally so the code path stays reachable.
const IS_DEV =
  typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
const TURNSTILE_TEST_SITEKEY = '1x00000000000000000000AA';
const TURNSTILE_SITEKEY =
  process.env.REACT_APP_TURNSTILE_SITEKEY ||
  (IS_DEV ? TURNSTILE_TEST_SITEKEY : '');

// Server-side verification of the Turnstile token pairs against an `action`
// string. Render-time and submit-time agree on this value so the verify
// endpoint can reject tokens that came from a different page/origin.
const TURNSTILE_ACTION = 'lead_submit';

// Module-scope state outlives any re-render of this component.
let turnstileWidgetId = null;

if (typeof window !== 'undefined') {
  window.__cabanaBotDefense = {
    // Read the current token directly from the Turnstile API rather than
    // from a callback-mutated module variable. The callback path proved
    // unreliable on the Vercel preview deploy (Turnstile writes the token
    // to its hidden input + getResponse() but does not always fire the
    // configured callback); reading via getResponse() is the canonical
    // synchronous getter and matches whatever the widget currently has.
    getTurnstileToken: () => {
      try {
        if (!window.turnstile || turnstileWidgetId === null) return null;
        const t = window.turnstile.getResponse(turnstileWidgetId);
        return t || null;
      } catch (_e) {
        return null;
      }
    },
  };
}

export default function BotDefense() {
  const containerRef = useRef(null);
  // True only while Cloudflare is showing an interactive challenge. In
  // interaction-only mode that is rare (suspicious sessions); when it
  // happens we surface the widget as a centered modal instead of a stray
  // corner box. Idle = false = overlay is transparent and click-through.
  const [challengeActive, setChallengeActive] = useState(false);

  useEffect(() => {
    if (!TURNSTILE_SITEKEY) {
      // Env not configured. Surface once so a missing prod env doesn't
      // silently leave us with no bot defense.
      try {
        // eslint-disable-next-line no-console
        console.warn(
          '[BotDefense] REACT_APP_TURNSTILE_SITEKEY missing; widget will not render',
        );
      } catch (_e) {}
      return undefined;
    }

    // Inject Turnstile script once per document. ?render=explicit tells the
    // api.js loader that we will call turnstile.render() ourselves, so it
    // does not auto-scan the DOM for `.cf-turnstile` divs and double-render.
    const existing = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]',
    );
    if (!existing) {
      const s = document.createElement('script');
      s.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    let cancelled = false;
    const tryRender = (attempt = 0) => {
      if (cancelled) return;
      if (turnstileWidgetId !== null) return;

      // We can render only once both the api.js global and our host node
      // are ready. Either being absent means "try again shortly", not
      // "give up", because the script tag was just injected.
      const ready = !!window.turnstile && !!containerRef.current;
      if (ready) {
        try {
          turnstileWidgetId = window.turnstile.render(containerRef.current, {
            sitekey: TURNSTILE_SITEKEY,
            // Bind the issued token to this specific submission flow. The
            // server compares this against body.action in the Siteverify
            // response so a token minted on a different page/origin can't
            // be replayed against /api/lead-submit.
            action: TURNSTILE_ACTION,
            // Auto-refresh expired tokens (managed mode tokens live 300s).
            // Multi-step forms exceed that window often.
            'refresh-expired': 'auto',
            // interaction-only keeps the widget INVISIBLE for the vast
            // majority of (legitimate) visitors. Cloudflare only renders an
            // interactive challenge when it flags the session as suspicious.
            // The earlier "too aggressive" note was a misdiagnosis: it came
            // from trying to hide the widget via a 1x1 container (which does
            // break the iframe). appearance:'interaction-only' is the real,
            // supported way to hide it, and pairs with the callbacks below.
            appearance: 'interaction-only',
            // Show the centered modal the instant an interactive challenge
            // starts.
            'before-interactive-callback': () => setChallengeActive(true),
            // Hide it on every terminal signal Turnstile exposes. Each of
            // these callbacks is individually unreliable (the same reason
            // getTurnstileToken reads via getResponse() rather than trusting
            // a callback, see note above). A real visitor reported the box
            // staying stuck after solving because after-interactive-callback
            // no-showed, so we wire ALL of them plus a polling watchdog
            // (below); whichever fires first closes the modal, and the user
            // can always close it by hand. Nobody gets trapped.
            'after-interactive-callback': () => setChallengeActive(false),
            callback: () => setChallengeActive(false),
            'expired-callback': () => setChallengeActive(false),
            'timeout-callback': () => setChallengeActive(false),
            'error-callback': () => setChallengeActive(false),
            // Compact keeps the challenge small enough to center cleanly on
            // any phone without overflow, while staying tappable on desktop.
            size: 'compact',
            theme: 'light',
          });
        } catch (_e) {
          // render() can throw on a transient init race (script half-loaded,
          // duplicate-render guard hot, etc.). Keep retrying within the
          // attempt budget rather than treating one failure as terminal.
        }
      }
      if (turnstileWidgetId === null && attempt < 60) {
        // Up to ~12s of polling (60 * 200ms) for api.js to finish loading
        // and any transient render exceptions to clear.
        setTimeout(() => tryRender(attempt + 1), 200);
      }
    };
    tryRender();

    return () => {
      cancelled = true;
    };
  }, []);

  // Watchdog: the modal must never trap a real visitor. While a challenge is
  // shown, poll Turnstile's canonical getResponse() and close the moment a
  // token exists (the success callback can no-show), and hard-close after a
  // ceiling so a silent failure can't leave the box stuck on screen. Escape
  // also closes it. Fail-open: the form submits with or without a token and
  // the server decides, so closing early never blocks a legitimate lead.
  useEffect(() => {
    if (!challengeActive) return undefined;
    const startedAt = Date.now();
    const poll = setInterval(() => {
      let token = null;
      try {
        if (window.turnstile && turnstileWidgetId !== null) {
          token = window.turnstile.getResponse(turnstileWidgetId) || null;
        }
      } catch (_e) {}
      if (token || Date.now() - startedAt > 30000) {
        setChallengeActive(false);
      }
    }, 300);
    const onKey = (e) => {
      if (e.key === 'Escape') setChallengeActive(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearInterval(poll);
      window.removeEventListener('keydown', onKey);
    };
  }, [challengeActive]);

  if (!TURNSTILE_SITEKEY) return null;

  const dismiss = () => setChallengeActive(false);

  // The host node for the widget must stay mounted at all times (Turnstile
  // drops its token when its node unmounts), so the overlay is always in the
  // DOM. When idle it is fully transparent and click-through so it never
  // covers the form; the inner widget renders nothing under interaction-only.
  // When a challenge is active we fade in a centered backdrop + card, so the
  // challenge reads as an intentional "confirm you're human" step and centers
  // correctly on both mobile and desktop.
  return (
    <div
      data-testid="cabana-bot-defense-overlay"
      aria-hidden={!challengeActive}
      onClick={(e) => {
        // A click on the backdrop itself (never on the card) closes the
        // modal, the same escape hatch users expect from any dialog.
        if (e.target === e.currentTarget) dismiss();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        boxSizing: 'border-box',
        pointerEvents: challengeActive ? 'auto' : 'none',
        background: challengeActive ? 'rgba(15, 23, 42, 0.55)' : 'transparent',
        transition: 'background 150ms ease',
      }}
    >
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          width: 'fit-content',
          maxWidth: 'min(94vw, 360px)',
          padding: challengeActive ? '24px 20px' : 0,
          borderRadius: 16,
          background: challengeActive ? '#ffffff' : 'transparent',
          boxShadow: challengeActive
            ? '0 20px 60px rgba(15, 23, 42, 0.28)'
            : 'none',
        }}
      >
        {challengeActive && (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              borderRadius: 9999,
              background: 'transparent',
              color: '#64748b',
              fontSize: 20,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        )}
        {challengeActive && (
          <p
            style={{
              margin: 0,
              textAlign: 'center',
              fontSize: 15,
              lineHeight: 1.4,
              fontWeight: 600,
              color: '#0f172a',
            }}
          >
            Just confirming you&apos;re human
          </p>
        )}
        {/* The widget node stays mounted at all times (Turnstile drops its
            token if its host unmounts), but we gate its visibility on the
            challenge state. opacity (not size) is used deliberately: a
            zero-size container breaks the Turnstile iframe, whereas opacity 0
            keeps it intact while guaranteeing no residual challenge UI lingers
            on screen once we have decided the challenge is done. */}
        <div
          ref={containerRef}
          data-testid="cabana-bot-defense"
          style={{
            opacity: challengeActive ? 1 : 0,
            pointerEvents: challengeActive ? 'auto' : 'none',
          }}
        />
      </div>
    </div>
  );
}
